import React, { useRef, useState } from "react";
import { Link2, Upload, Clock, Key, FileText, Loader2, X, Check, MessageCircle } from "lucide-react";
import { uploadSoftware } from "../lib/api";
import { toast } from "sonner";

const DURATIONS = ["1 day", "3 days", "1 week", "1 month", "Lifetime"];

/**
 * "Software Access" section embedded inside the order form. The buyer picks
 * how to deliver their loader (link / file upload / send-later), pastes the
 * license key + duration, and writes setup instructions for the creator.
 *
 * Controlled component: parent owns `value` and receives changes through `onChange`.
 *   value = { method, url, license_key, license_duration, instructions }
 */
export const SoftwareAccessSection = ({ value, onChange }) => {
  const v = value || { method: "later", url: "", license_key: "", license_duration: "", instructions: "" };
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filename, setFilename] = useState("");
  const [customDuration, setCustomDuration] = useState(false);

  const set = (patch) => onChange({ ...v, ...patch });

  const onPickFile = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large (max 100 MB).");
      return;
    }
    setUploading(true);
    setProgress(0);
    setFilename(file.name);
    try {
      const res = await uploadSoftware(file, setProgress);
      set({ method: "file", url: res.url });
      toast.success(`Uploaded · ${file.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clearFile = () => {
    set({ method: "later", url: "" });
    setFilename("");
  };

  return (
    <section data-testid="order-software-section" className="cc-surface rounded-xl p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Key size={14} className="text-[#00ff88]" />
        <div className="cc-overline">Software access</div>
        <span className="text-[10px] text-[#606070] uppercase tracking-widest ml-auto">Private — creator only</span>
      </div>

      {/* Method selector */}
      <div data-testid="order-software-method" className="grid grid-cols-3 gap-2">
        <MethodCard
          active={v.method === "link"}
          onClick={() => set({ method: "link", url: "" })}
          icon={Link2}
          title="Link"
          sub="Paste a URL"
          testId="software-method-link"
        />
        <MethodCard
          active={v.method === "file"}
          onClick={() => { set({ method: "file" }); inputRef.current?.click(); }}
          icon={Upload}
          title="Upload"
          sub=".exe · .zip · .rar"
          testId="software-method-file"
        />
        <MethodCard
          active={v.method === "later"}
          onClick={() => set({ method: "later", url: "" })}
          icon={MessageCircle}
          title="Send later"
          sub="Via chat"
          testId="software-method-later"
        />
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".exe,.dll,.zip,.rar,.7z,.msi,.bat,.cmd,.ps1,.vbs,.jar,.apk,.dmg,.iso"
        className="hidden"
        data-testid="software-file-input"
        onChange={(e) => onPickFile(e.target.files?.[0])}
      />

      {v.method === "link" && (
        <div>
          <input
            data-testid="software-link-input"
            value={v.url || ""}
            onChange={(e) => set({ url: e.target.value })}
            placeholder="https://… (loader, installer, download URL)"
            className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] font-mono cc-focus-ring"
          />
        </div>
      )}

      {v.method === "file" && (
        <div className="rounded-lg border border-[#15152a] bg-[#06060c] p-3 flex items-center gap-3">
          {uploading ? (
            <>
              <Loader2 size={16} className="text-[#00ff88] animate-spin shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{filename}</div>
                <div className="w-full h-1 bg-[#15152a] rounded-full mt-1 overflow-hidden">
                  <div className="h-full bg-[#00ff88] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <span className="text-[10px] font-mono text-[#a0a0b0]">{progress}%</span>
            </>
          ) : v.url ? (
            <>
              <Check size={14} className="text-[#00ff88] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{filename || v.url.split("/").pop()}</div>
                <div className="text-[11px] text-[#606070] truncate font-mono">{v.url}</div>
              </div>
              <button
                type="button"
                onClick={clearFile}
                className="text-[#606070] hover:text-white"
                data-testid="software-file-clear"
                aria-label="Remove file"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full text-sm text-[#00ff88] hover:underline text-left inline-flex items-center gap-2"
            >
              <Upload size={14} /> Click to choose a file (max 100 MB)
            </button>
          )}
        </div>
      )}

      {/* License key + duration */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="cc-overline mb-1.5 inline-flex items-center gap-1.5">
            <Key size={11} /> License key
          </div>
          <input
            data-testid="software-license-key"
            value={v.license_key || ""}
            onChange={(e) => set({ license_key: e.target.value })}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] font-mono cc-focus-ring"
          />
        </div>
        <div>
          <div className="cc-overline mb-1.5 inline-flex items-center gap-1.5">
            <Clock size={11} /> Key duration
          </div>
          {customDuration ? (
            <div className="flex gap-2">
              <input
                data-testid="software-duration-custom"
                value={v.license_duration || ""}
                onChange={(e) => set({ license_duration: e.target.value })}
                placeholder="e.g. 14 days"
                className="flex-1 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] font-mono cc-focus-ring"
              />
              <button
                type="button"
                onClick={() => { setCustomDuration(false); set({ license_duration: "" }); }}
                className="text-[#606070] hover:text-white text-xs"
              >
                Presets
              </button>
            </div>
          ) : (
            <div data-testid="software-duration" className="flex flex-wrap gap-1.5">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => set({ license_duration: d })}
                  data-testid={`software-duration-${d.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-2.5 py-1 rounded-md text-[11px] border transition ${
                    v.license_duration === d
                      ? "bg-[#00ff88]/10 text-[#00ff88] border-[#00ff88]/30"
                      : "border-[#15152a] text-[#a0a0b0] hover:text-white"
                  }`}
                >
                  {d}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setCustomDuration(true); set({ license_duration: "" }); }}
                className="px-2.5 py-1 rounded-md text-[11px] border border-dashed border-[#15152a] text-[#606070] hover:text-white"
              >
                Custom…
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div>
        <div className="cc-overline mb-1.5 inline-flex items-center gap-1.5">
          <FileText size={11} /> Setup instructions
        </div>
        <textarea
          data-testid="software-instructions"
          rows={3}
          value={v.instructions || ""}
          onChange={(e) => set({ instructions: e.target.value })}
          placeholder="How to install, inject, configure your software. Include any special steps the creator needs to follow."
          className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] cc-focus-ring"
        />
      </div>

      <p className="text-[11px] text-[#606070] leading-relaxed">
        This information is encrypted at rest and only the assigned creator can read it.
        {v.method === "later" && " You can also paste it later from the chat panel."}
      </p>
    </section>
  );
};

const MethodCard = ({ active, onClick, icon: Icon, title, sub, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left rounded-lg p-3 border transition-colors ${
      active
        ? "border-[#00ff88]/40 bg-[#00ff88]/5"
        : "border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a]"
    }`}
  >
    <div className={`text-sm font-semibold ${active ? "text-[#00ff88]" : "text-white"} flex items-center gap-1.5`}>
      <Icon size={13} /> {title}
    </div>
    <div className="text-[10px] text-[#606070] mt-0.5">{sub}</div>
  </button>
);

export default SoftwareAccessSection;
